import React, { useState, useRef } from 'react';
import {
  FileText, Upload, Download, CheckCircle, XCircle,
  Search, FileCheck, Sparkles, AlertCircle, Copy
} from 'lucide-react';
import {
  detectFormFields, autoDetectFieldTypes, extractFormDataAsJSON,
  validateFormFields
} from '../utils/formDetectionUtils';
import { saveAs } from 'file-saver';
import { toast } from 'react-toastify';
import ToolActionButtons from './shared/ToolActionButtons';

const FormDetection = () => {
  const [file, setFile] = useState(null);
  const [detectedFields, setDetectedFields] = useState(null);
  const [fieldSuggestions, setFieldSuggestions] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [validationResults, setValidationResults] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileUpload = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.type === 'application/pdf') {
      setFile(selectedFile);
      detectForms(selectedFile);
    } else {
      toast.error('Please upload a PDF file');
    }
  };

  const detectForms = async (pdfFile) => {
    try {
      setIsProcessing(true);
      setProgress(0);
      const result = await detectFormFields(pdfFile, setProgress);
      setDetectedFields(result);

      // Auto-detect field types
      if (result.fields.length > 0) {
        const suggestions = await autoDetectFieldTypes(result.fields);
        setFieldSuggestions(suggestions);
      }

      toast.success(`Detected ${result.totalFields} form fields!`);
    } catch (error) {
      toast.error(`Form detection failed: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleExportJSON = async () => {
    if (!file) return;

    try {
      setIsProcessing(true);
      setProgress(0);
      const result = await extractFormDataAsJSON(file, setProgress);
      saveAs(result.blob, result.filename);
      toast.success('Form data exported as JSON!');
    } catch (error) {
      toast.error(`Export failed: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleValidate = async () => {
    if (!file || !detectedFields) return;

    // Create basic validation rules
    const validationRules = {};
    detectedFields.fields.forEach(field => {
      validationRules[field.name] = {
        required: field.required,
        type: fieldSuggestions.find(s => s.fieldName === field.name)?.suggestedType || 'text',
      };
    });

    try {
      setIsProcessing(true);
      setProgress(0);
      const result = await validateFormFields(file, validationRules, setProgress);
      setValidationResults(result);
      toast.success(`Validation complete: ${result.isValid ? 'All fields valid' : 'Some fields have errors'}`);
    } catch (error) {
      toast.error(`Validation failed: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900 text-white p-6">
      {/* Action Buttons */}
      <ToolActionButtons
        onAdd={() => {
          fileInputRef.current?.click();
        }}
        notificationCount={detectedFields?.totalFields || 0}
      />

      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="tool-header">
          <div className="tool-icon-wrapper">
            <FileCheck size={40} color="var(--color-text-primary)" />
          </div>
          <div className="tool-header-content">
            <h1 className="tool-title">Automatic Form Detection</h1>
            <p className="tool-description">
              AI-powered form field detection and extraction
            </p>
          </div>
        </div>

        {/* File Upload */}
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 mb-6">
          <div className="flex items-center gap-4">
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf"
              onChange={handleFileUpload}
              className="hidden"
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              className="btn-primary flex items-center gap-2"
            >
              <Upload size={20} />
              {file ? 'Change PDF' : 'Upload PDF to Detect Forms'}
            </button>
            {file && (
              <div className="flex items-center gap-2 text-gray-300">
                <FileText size={20} />
                <span>{file.name}</span>
              </div>
            )}
          </div>
        </div>

        {/* Progress Bar */}
        {isProcessing && (
          <div className="mb-6">
            <div className="bg-white/5 rounded-full h-2 overflow-hidden">
              <div
                className="bg-gradient-to-r from-blue-500 to-cyan-500 h-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
            <p className="text-sm text-gray-400 mt-2">Processing... {Math.round(progress)}%</p>
          </div>
        )}

        {/* Results */}
        {detectedFields && (
          <div className="space-y-6">
            {/* Summary Card */}
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold">Detection Results</h2>
                <div className="flex items-center gap-2">
                  {detectedFields.hasFormFields ? (
                    <CheckCircle className="text-green-400" size={24} />
                  ) : (
                    <XCircle className="text-red-400" size={24} />
                  )}
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-white/5 rounded-lg p-4">
                  <div className="text-3xl font-black text-blue-400">{detectedFields.totalFields}</div>
                  <div className="text-gray-400 text-sm">Total Fields</div>
                </div>
                <div className="bg-white/5 rounded-lg p-4">
                  <div className="text-3xl font-black text-green-400">
                    {detectedFields.fields.filter(f => f.required).length}
                  </div>
                  <div className="text-gray-400 text-sm">Required Fields</div>
                </div>
                <div className="bg-white/5 rounded-lg p-4">
                  <div className="text-3xl font-black text-purple-400">
                    {new Set(detectedFields.fields.map(f => f.type)).size}
                  </div>
                  <div className="text-gray-400 text-sm">Field Types</div>
                </div>
              </div>
            </div>

            {/* Detected Fields */}
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold">Detected Fields</h2>
                <div className="flex gap-2">
                  <button
                    onClick={handleExportJSON}
                    className="btn-primary flex items-center gap-2"
                    disabled={isProcessing}
                  >
                    <Download size={18} />
                    Export JSON
                  </button>
                  <button
                    onClick={handleValidate}
                    className="btn-outline flex items-center gap-2"
                    disabled={isProcessing}
                  >
                    <CheckCircle size={18} />
                    Validate
                  </button>
                </div>
              </div>

              <div className="space-y-4">
                {detectedFields.fields.map((field, index) => {
                  const suggestion = fieldSuggestions.find(s => s.fieldName === field.name);
                  return (
                    <div
                      key={index}
                      className="bg-white/5 rounded-lg p-4 border border-white/10"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <h3 className="font-bold text-lg">{field.name}</h3>
                          {field.required && (
                            <span className="bg-red-500/20 text-red-400 text-xs px-2 py-1 rounded">
                              Required
                            </span>
                          )}
                          <span className="bg-blue-500/20 text-blue-400 text-xs px-2 py-1 rounded">
                            {field.type}
                          </span>
                        </div>
                        {suggestion && (
                          <div className="flex items-center gap-2">
                            <Sparkles size={16} className="text-yellow-400" />
                            <span className="text-xs text-gray-400">
                              Suggested: {suggestion.suggestedType}
                            </span>
                          </div>
                        )}
                      </div>

                      <div className="mt-2 space-y-2">
                        <div className="text-sm text-gray-400">
                          <strong>Type:</strong> {field.type}
                        </div>
                        {field.value !== undefined && field.value !== null && (
                          <div className="text-sm text-gray-300">
                            <strong>Current Value:</strong> {String(field.value)}
                          </div>
                        )}
                        {field.options && (
                          <div className="text-sm text-gray-300">
                            <strong>Options:</strong> {field.options.join(', ')}
                          </div>
                        )}
                        {field.readOnly && (
                          <div className="text-xs text-orange-400 flex items-center gap-1">
                            <AlertCircle size={14} />
                            Read-only field
                          </div>
                        )}
                      </div>

                      {validationResults && (
                        <div className="mt-3 pt-3 border-t border-white/10">
                          {validationResults.results[index] && (
                            <div>
                              {validationResults.results[index].isValid ? (
                                <div className="flex items-center gap-2 text-green-400 text-sm">
                                  <CheckCircle size={16} />
                                  Valid
                                </div>
                              ) : (
                                <div className="space-y-1">
                                  <div className="flex items-center gap-2 text-red-400 text-sm">
                                    <XCircle size={16} />
                                    Invalid
                                  </div>
                                  {validationResults.results[index].errors.map((error, i) => (
                                    <div key={i} className="text-xs text-red-300 ml-6">
                                      • {error}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* JSON Preview */}
            {detectedFields && (
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-2xl font-bold">Form Data (JSON)</h2>
                  <button
                    onClick={() => copyToClipboard(JSON.stringify(detectedFields, null, 2))}
                    className="btn-outline flex items-center gap-2"
                  >
                    <Copy size={18} />
                    Copy
                  </button>
                </div>
                <pre className="bg-black/30 rounded-lg p-4 overflow-x-auto text-sm">
                  {JSON.stringify(detectedFields, null, 2)}
                </pre>
              </div>
            )}
          </div>
        )}

        {!detectedFields && !isProcessing && (
          <div className="text-center py-12 text-gray-400">
            <FileCheck size={64} className="mx-auto mb-4 opacity-50" />
            <p>Upload a PDF file to detect form fields automatically</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default FormDetection;


